import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Loader2, Eye, Copy, Star, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { StockBadge, formatGBP } from "../../components/common/StockBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import api, { formatApiError } from "../../lib/api";

const selectCls = "bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm";

export default function AdminProducts() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ category_id: "", stock_status: "", is_active: "", is_featured: "", sort: "updated", page: 1 });

  const load = useCallback(() => {
    setLoading(true);
    const params = { sort: filters.sort, page: filters.page, limit: 20 };
    if (search) params.search = search;
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.stock_status) params.stock_status = filters.stock_status;
    if (filters.is_active !== "") params.is_active = filters.is_active;
    if (filters.is_featured !== "") params.is_featured = filters.is_featured;
    api.get("/admin/products", { params }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, search]);

  useEffect(() => { api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: k === "page" ? v : 1 }));

  const patch = async (id, body, msg) => {
    try { await api.patch(`/admin/products/${id}`, body); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const duplicate = async (id) => {
    try { const { data } = await api.post(`/admin/products/${id}/duplicate`); toast.success("Duplicated as draft"); navigate(`/admin/products/${data.product_id}/edit`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Permanently delete "${p.name}"? This removes the product and all its images and cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/products/${p.product_id}`);
      toast.success(`"${p.name}" deleted${data?.images_removed ? ` (${data.images_removed} image${data.images_removed > 1 ? "s" : ""} removed)` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not delete product"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Products</h1>
          <p className="mt-2 text-muted-foreground">{data.total} product{data.total !== 1 ? "s" : ""} in the catalogue.</p>
        </div>
        <Button data-testid="add-product-btn" onClick={() => navigate("/admin/products/new")} className="cf-gold-btn font-semibold gap-2"><Plus className="h-4 w-4" /> Add Product</Button>
      </div>

      <GlassCard className="p-4" hover={false}>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input data-testid="product-search" placeholder="Search name, SKU, barcode, brand…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-black/30 border-white/15 text-white" />
          </div>
          <select data-testid="filter-category" className={selectCls} value={filters.category_id} onChange={(e) => setF("category_id", e.target.value)}>
            <option value="">All categories</option>
            {cats.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
          <select data-testid="filter-stock" className={selectCls} value={filters.stock_status} onChange={(e) => setF("stock_status", e.target.value)}>
            <option value="">All stock</option>
            <option value="in_stock">In Stock</option>
            <option value="out_of_stock">Out of Stock</option>
            <option value="coming_soon">Coming Soon</option>
          </select>
          <select data-testid="filter-active" className={selectCls} value={filters.is_active} onChange={(e) => setF("is_active", e.target.value)}>
            <option value="">Active & inactive</option>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </select>
          <select data-testid="filter-featured" className={selectCls} value={filters.is_featured} onChange={(e) => setF("is_featured", e.target.value)}>
            <option value="">All</option>
            <option value="true">Featured</option>
          </select>
          <select data-testid="sort-products" className={selectCls} value={filters.sort} onChange={(e) => setF("sort", e.target.value)}>
            <option value="updated">Recently updated</option>
            <option value="name-asc">Name A–Z</option>
            <option value="price-asc">Price low–high</option>
            <option value="price-desc">Price high–low</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </GlassCard>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No products match your filters.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/70">Product</TableHead>
                <TableHead className="text-white/70">SKU</TableHead>
                <TableHead className="text-white/70">Category</TableHead>
                <TableHead className="text-white/70">Price</TableHead>
                <TableHead className="text-white/70">Stock</TableHead>
                <TableHead className="text-white/70">Active</TableHead>
                <TableHead className="text-white/70">Featured</TableHead>
                <TableHead className="text-white/70 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((p) => {
                const cat = cats.find((c) => c.category_id === p.category_id);
                const img = (p.images || []).find((m) => m.is_primary) || (p.images || [])[0];
                return (
                  <TableRow key={p.product_id} className="border-white/10" data-testid={`product-row-${p.sku}`}>
                    <TableCell className="text-white font-medium">
                      <div className="flex items-center gap-3">
                        {img ? <img src={img.thumb_url || img.url} alt="" className="h-9 w-9 rounded object-cover" /> : <div className="h-9 w-9 rounded bg-white/10" />}
                        <span className="line-clamp-1">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">{p.sku}<br /><span className="text-white/30">{p.barcode}</span></TableCell>
                    <TableCell className="text-muted-foreground">{cat?.name || "—"}</TableCell>
                    <TableCell className="text-white">{formatGBP(p.retail_price)}</TableCell>
                    <TableCell><StockBadge status={p.stock_status} /></TableCell>
                    <TableCell>
                      <button data-testid={`toggle-active-${p.sku}`} onClick={() => patch(p.product_id, { is_active: !p.is_active }, "Updated")} className={p.is_active ? "text-emerald-400 text-sm" : "text-firered text-sm"}>{p.is_active ? "Active" : "Inactive"}</button>
                    </TableCell>
                    <TableCell>
                      <button data-testid={`toggle-featured-${p.sku}`} onClick={() => patch(p.product_id, { is_featured: !p.is_featured }, "Updated")} title="Toggle featured">
                        <Star className={p.is_featured ? "h-4 w-4 text-gold fill-gold" : "h-4 w-4 text-white/30"} />
                      </button>
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      <a href={`/product/${p.slug}`} target="_blank" rel="noreferrer" title="Preview" data-testid={`preview-product-${p.sku}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Eye className="h-4 w-4" /></a>
                      <button onClick={() => duplicate(p.product_id)} title="Duplicate" data-testid={`duplicate-product-${p.sku}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Copy className="h-4 w-4" /></button>
                      <button onClick={() => navigate(`/admin/products/${p.product_id}/edit`)} title="Edit" data-testid={`edit-product-${p.sku}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(p)} title="Delete" data-testid={`delete-product-${p.sku}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-firered hover:bg-firered/10"><Trash2 className="h-4 w-4" /></button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </GlassCard>

      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="product-pagination">
          <Button variant="outline" className="border-white/20 text-white" disabled={filters.page <= 1} onClick={() => setF("page", filters.page - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {data.page} of {data.pages}</span>
          <Button variant="outline" className="border-white/20 text-white" disabled={filters.page >= data.pages} onClick={() => setF("page", filters.page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
